// Phase H.14a — Inline PDF preview for uploaded templates.
//
// Detail page for an uploaded template (`source_type === "upload"`) gets
// an inline render of the PDF above the metadata card. We fetch the
// source via an authenticated request (the existing
// `/admin/templates/{id}/download-source` endpoint is auth-gated), turn
// the response blob into an object URL, and feed that to a sandboxed
// iframe. Browsers' built-in PDF viewers handle the rest.
//
// For non-PDF uploads (e.g. .docx) we fall back to a polite message and
// rely on the existing Source download button on the parent page.

import React, { useEffect, useRef, useState } from "react";
import { Download, FileWarning, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PdfPreview from "@/components/PdfPreview";

function isPdfUpload(template) {
    if (!template) return false;
    const mime = (template.original_mime || "").toLowerCase();
    if (mime.includes("pdf")) return true;
    const name = (template.original_filename || "").toLowerCase();
    return name.endsWith(".pdf");
}

export default function UploadedTemplatePreview({ template }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const currentUrlRef = useRef(null);

    const isPdf = isPdfUpload(template);
    const id = template?.id;

    useEffect(() => {
        // Always release the previous object URL before swapping.
        const release = () => {
            if (currentUrlRef.current) {
                URL.revokeObjectURL(currentUrlRef.current);
                currentUrlRef.current = null;
            }
        };
        if (!id || !isPdf) {
            release();
            setBlobUrl(null);
            return release;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                // Phase H.14a — hit the endpoint WITHOUT ?download=1 so the
                // backend returns `Content-Disposition: inline`. Combined
                // with the explicit `application/pdf` MIME on the Blob
                // wrapper below, the browser's built-in PDF viewer renders
                // the file inside the iframe. (Auth is Bearer-only — we
                // can't point the iframe at the endpoint directly, hence
                // the blob-URL hop.)
                const resp = await api.get(
                    `/admin/templates/${id}/download-source`,
                    { responseType: "blob" }
                );
                if (cancelled) return;
                const blob = new Blob([resp.data], { type: "application/pdf" });
                release();
                const url = URL.createObjectURL(blob);
                currentUrlRef.current = url;
                setBlobUrl(url);
            } catch (err) {
                if (cancelled) return;
                setError(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            release();
        };
        // We intentionally key on id + isPdf only; template ref churn from
        // unrelated metadata edits shouldn't re-fetch the PDF.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isPdf]);

    const onDownload = async () => {
        try {
            // Phase H.14a — explicitly ask the backend for an `attachment`
            // disposition so the browser saves rather than re-opening the
            // preview in a new tab.
            const resp = await api.get(
                `/admin/templates/${id}/download-source?download=1`,
                { responseType: "blob" }
            );
            const filename =
                template?.original_filename || `${template?.name || "template"}.pdf`;
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!template) return null;

    if (!isPdf) {
        return (
            <div
                data-testid="uploaded-template-preview-fallback"
                className="mb-6 rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900"
            >
                <div className="flex items-start gap-3">
                    <FileWarning size={18} className="mt-0.5 shrink-0" />
                    <div>
                        <p className="font-medium">Inline preview unavailable for this file type.</p>
                        <p className="text-xs text-amber-800 mt-0.5">
                            {template.original_filename
                                ? `Source: ${template.original_filename}`
                                : "Source file type does not support inline preview."}
                            {" "}Click <strong>Source</strong> in the page header to download the original.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="uploaded-template-preview"
            className="mb-6 rounded-lg border border-slate-200 bg-white overflow-hidden"
        >
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-600 truncate">
                    Preview of{" "}
                    <span className="font-mono text-slate-900" data-testid="uploaded-template-preview-filename">
                        {template.original_filename || `${template.name}.pdf`}
                    </span>
                </p>
                <button
                    type="button"
                    onClick={onDownload}
                    data-testid="uploaded-template-preview-download"
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-900 hover:text-blue-700 hover:underline"
                >
                    <Download size={12} /> Download
                </button>
            </div>
            <div
                className="relative bg-slate-100"
                style={{ minHeight: 200 }}
            >
                {loading && (
                    <div
                        data-testid="uploaded-template-preview-loading"
                        className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-500 text-sm"
                    >
                        <Loader2 size={20} className="animate-spin" />
                        Loading PDF…
                    </div>
                )}
                {error && !loading && (
                    <div
                        data-testid="uploaded-template-preview-error"
                        className="absolute inset-0 flex items-center justify-center p-4 text-red-700 text-sm text-center"
                    >
                        Could not load PDF preview: {error}
                    </div>
                )}
                {blobUrl && !error && (
                    <PdfPreview
                        key={blobUrl}
                        src={blobUrl}
                        downloadHref={blobUrl}
                        downloadName={template.original_filename || `${template.name}.pdf`}
                        testid="uploaded-template-preview-canvas"
                    />
                )}
            </div>
        </div>
    );
}
